import React, { useEffect, useState } from 'react';
import Section from '../common/Section/Section';
import { useParams } from 'react-router-dom';
import LoadingPage from '../LoadingPage';
import axios from 'axios';
import { ComparisonDetailMembersDataType, MemberDetailResponseType } from '../../types';
import DirectMemberComparisons from './DirectMemberComparisons';
import { redirectErrorPage } from '../../utils/errors';
import MatchStatSection from './MatchStatSection';
import VotingComparison from './VotingComparison';
import MatchChart from './MatchChartSection';

type Props = {
    firstMemberId: number;
    secondMemberId: number;
};

const transformDateToApiFormat = (date: string | undefined | null) => {
    if (!date) return null;
    const dateParts = date.split('.');
    return dateParts[2] + '-' + dateParts[1] + '-' + dateParts[0];
};

const calculateDateRange = (firstMemberData: MemberDetailResponseType, secondMemberData: MemberDetailResponseType) => {
    if (!firstMemberData || !secondMemberData) {
        return { dateFrom: '', dateTo: '' };
    }

    const firstMemberFrom = firstMemberData.politicke_strany.at(-1)?.datum_od;
    const secondMemberFrom = secondMemberData.politicke_strany.at(-1)?.datum_od;

    const firstMemberTo = firstMemberData.politicke_strany[0]?.datum_do;
    const secondMemberTo = secondMemberData.politicke_strany[0]?.datum_do;

    const dateFrom =
        !firstMemberFrom || (secondMemberFrom && firstMemberFrom < secondMemberFrom)
            ? secondMemberFrom
            : firstMemberFrom;
    const dateTo =
        !secondMemberTo || (firstMemberTo && firstMemberTo > secondMemberTo) ? firstMemberTo : secondMemberTo ?? '';

    return { dateFrom, dateTo };
};

const ComparisonDetail = ({ firstMemberId, secondMemberId }: Props) => {
    const { municipalitaId, bodyType, zastupitelstvoId } = useParams();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<ComparisonDetailMembersDataType>({
        firstMember: null,
        secondMember: null,
        comparison: null
    });

    useEffect(() => {
        const fetchDataForBothMembers = async () => {
            setLoading(true);
            try {
                const [firstMemberData, secondMemberData] = await Promise.all([
                    axios
                        .get<MemberDetailResponseType>(
                            `${municipalitaId}/${bodyType}/${zastupitelstvoId}/member/${firstMemberId}`
                        )
                        .then((response) => response.data),
                    axios
                        .get<MemberDetailResponseType>(
                            `${municipalitaId}/${bodyType}/${zastupitelstvoId}/member/${secondMemberId}`
                        )
                        .then((response) => response.data)
                ]);

                const { dateFrom, dateTo } = calculateDateRange(firstMemberData, secondMemberData);

                axios
                    .get(
                        `${municipalitaId}/${bodyType}/${zastupitelstvoId}/compare?prvni_clen=${firstMemberId}&druhy_clen=${secondMemberId}&datum_od=${transformDateToApiFormat(
                            dateFrom
                        )}&datum_do=${transformDateToApiFormat(dateTo)}`
                    )
                    .then((response) => {
                        setData({
                            comparison: response.data,
                            firstMember: firstMemberData,
                            secondMember: secondMemberData
                        });
                    });
            } catch (error) {
                redirectErrorPage(error);
            } finally {
                setTimeout(() => {
                    setLoading(false);
                }, 333);
            }
        };

        fetchDataForBothMembers();
        // Including data in the dependency array would cause an infinite loop
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [firstMemberId, secondMemberId, municipalitaId, bodyType, zastupitelstvoId]);

    if (loading || data.firstMember === null || data.secondMember === null) {
        return <LoadingPage />;
    }
    return (
        <>
            <Section>
                <DirectMemberComparisons {...data} />
                <MatchStatSection
                    comparison={data.comparison}
                    {...calculateDateRange(data.firstMember, data.secondMember)}
                />
            </Section>
            <VotingComparison comparison={data.comparison} />
            <MatchChart comparison={data.comparison} />
        </>
    );
};

export default ComparisonDetail;
